import { useEffect, useRef, useState } from 'react';

import { Modal, dialogService, toastService } from '@thefarmersfront/kitchen';
import { isDeepEqual } from '@thefarmersfront/kitchen-utensil';

import { useShallow } from 'zustand/react/shallow';

import { useGetReturnOrdersExistence, usePostReturnOrders } from '~/hooks/claim';
import { useGetOrderOutboundOrderCode } from '~/hooks/order';
import { initialClaimStoreValue, useClaimStore } from '~/store';
import { addIf, focusFirstErrorField, formatTel, validateTelFormat } from '~/utils';

import {
  DEFAULT_INPUT_STATUS,
  ERROR_INVALID_FORMAT_INPUT_STATUS,
  ERROR_REQUIRED_INPUT_STATUS,
  ERROR_REQUIRED_SELECT_STATUS,
  ERROR_STATUS,
  TOAST_MESSAGE,
} from '~/constants';
import { CLAIM_TOTAL_BOX_COUNT } from '~/constants/claim';

import { ClaimRegisterFirstStepContent } from '../../ClaimRegisterFirstStepContent';
import { ClaimReturnRegisterSecondStepContent } from './ClaimReturnRegisterSecondStepContent';

import type {
  TypePostReturnOrdersParams,
  TypeReturnRegisterSecondStepErrorFieldRefs,
  TypeReturnRegisterSecondStepValues,
  TypeReturnRegisterSecondStepValuesState,
} from '~/types/claim.types';
import type { TypeOrders } from '~/types/order.types/order.types';

const DEFAULT_ERROR_FIELD_REFS: TypeReturnRegisterSecondStepErrorFieldRefs = {
  totalBox: null,
  totalBoxInput: null,
  receiverContactNumber: null,
  primaryAddress: null,
  returnPlace: null,
  accessMethod: null,
};

const ERROR_FIELD_FOCUS_ORDER: (keyof TypeReturnRegisterSecondStepErrorFieldRefs)[] = [
  'receiverContactNumber',
  'primaryAddress',
  'returnPlace',
  'totalBox',
  'totalBoxInput',
  'accessMethod',
];

const ClaimReturnRegisterModal = ({
  isOpen,
  onCancel,
  onConfirm,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const errorFieldRefs = useRef<TypeReturnRegisterSecondStepErrorFieldRefs>(DEFAULT_ERROR_FIELD_REFS);

  const { requestReturnOrdersExistence } = useGetReturnOrdersExistence();
  const { requestOrderDetail } = useGetOrderOutboundOrderCode();
  const { registerReturnOrders } = usePostReturnOrders();

  const selectOptions = useClaimStore((state) => state.selectOptions);

  const [
    /** step 1 */
    firstStepSearchFilterValues,
    setFirstStepSearchFilterValues,
    /** step 2 */
    secondStepReturnInfoValues,
    secondStepReturnInfoValuesState,
    setSecondStepReturnInfoValues,
    setSecondStepReturnInfoValuesState,
    /** 공통 */
    resetClaimStore,
  ] = useClaimStore(
    useShallow((state) => [
      state.registerFirstStepSearchFilterValues,
      state.actions.setRegisterFirstStepSearchFilterValues,
      state.returnRegisterSecondStepValues,
      state.returnRegisterSecondStepValuesState,
      state.actions.setReturnRegisterSecondStepValues,
      state.actions.setReturnRegisterSecondStepValuesState,
      state.actions.resetStore,
    ])
  );
  const defaultReturnInfoValues = initialClaimStoreValue.returnRegisterSecondStepValues;
  const defaultReturnInfoValuesState = initialClaimStoreValue.returnRegisterSecondStepValuesState;

  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [selectedOrder, setSelectedOrder] = useState<TypeOrders | null>(null);
  const [initialReturnInfoValues, setInitialReturnInfoValues] =
    useState<TypeReturnRegisterSecondStepValues>(defaultReturnInfoValues);

  useEffect(() => {
    setFirstStepSearchFilterValues({
      ...firstStepSearchFilterValues,
      keywordType: selectOptions.orderKeyword[0] ?? null,
      dateType: selectOptions.orderDateType[0] ?? null,
    });

    return () =>
      resetClaimStore([
        'registerFirstStepSearchFilterValues',
        'registerFirstStepSearchFilterValuesStatus',
        'registerFirstStepOrdersData',
        'returnRegisterSecondStepValues',
        'returnRegisterSecondStepValuesState',
      ]);
  }, []);

  const handleCancel = () => {
    const isModified = !isDeepEqual(initialReturnInfoValues, secondStepReturnInfoValues);

    if (isModified) {
      dialogService.addDialog({
        type: 'confirm',
        children: '작성된 내용이 저장되지 않습니다.\n페이지를 나가시겠습니까?',
        confirmText: '나가기',
        onConfirm: onCancel,
      });
      return;
    }

    onCancel();
  };

  const moveToStep2 = () => {
    if (!selectedOrder) return;

    requestOrderDetail(
      { outboundOrderCode: selectedOrder.outbound.outboundCode },
      {
        onSuccess: (data) => {
          const initialReturnInfoValues = {
            ...secondStepReturnInfoValues,
            outboundCode: data.outbound.outboundCode,
            outboundClusterCenter: data.outbound.clusterCenter.name,
            outboundCourier: data.delivery.courier.name,
            receiverName: data.receiver.name,
            receiverContactNumber: formatTel(data.receiver.phoneNumber),
            primaryAddress: data.receiver.address.primaryAddress,
            detailAddress: data.receiver.address.secondAddress,
            returnPlace: selectOptions.returnPlace[0],
            accessMethod: selectOptions.accessMethod[0],
            returnOrderItems: data.order.orderItems.map((item) => ({
              ...item,
              isReturn: false,
              returnQuantity: '',
              returnReason: null,
            })),
          };

          setSecondStepReturnInfoValues(initialReturnInfoValues);
          setInitialReturnInfoValues(initialReturnInfoValues);

          setSecondStepReturnInfoValuesState({
            ...secondStepReturnInfoValuesState,
            returnOrderItems: data.order.orderItems.map((item) => ({
              itemId: item.itemId,
              returnQuantity: DEFAULT_INPUT_STATUS,
              returnReason: DEFAULT_INPUT_STATUS,
            })),
          });
          setCurrentStep(2);
        },
        onError: (error) =>
          toastService.addToast({
            type: 'error',
            message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
          }),
      }
    );
  };

  const handleStepChange = (step: number) => {
    if (step === 1) {
      const isModified = !isDeepEqual(initialReturnInfoValues, secondStepReturnInfoValues);

      if (isModified) {
        dialogService.addDialog({
          type: 'confirm',
          children: '작성된 내용이 저장되지 않습니다.\n이전 페이지로 이동하시겠습니까?',
          confirmText: '이전으로',
          onConfirm: () => {
            setSelectedOrder(null);
            setSecondStepReturnInfoValues(defaultReturnInfoValues);
            setInitialReturnInfoValues(defaultReturnInfoValues);
            setSecondStepReturnInfoValuesState(defaultReturnInfoValuesState);
            setCurrentStep(1);
          },
        });
        return;
      }

      setCurrentStep(1);
    } else if (step === 2) {
      if (!selectedOrder) {
        dialogService.addDialog({
          children: '반품 신청할 주문을 선택해주세요.',
        });
        return;
      }

      requestReturnOrdersExistence(
        { outboundOrderCode: selectedOrder.outbound.outboundCode },
        {
          onSuccess: (isReturnExist) => {
            if (isReturnExist) {
              dialogService.addDialog({
                type: 'confirm',
                children: '이미 반품 또는 교환을 신청한 주문입니다.\n계속 신청하시겠습니까?',
                confirmText: '계속 신청',
                onConfirm: moveToStep2,
              });
              return;
            }

            moveToStep2();
          },
          onError: (error) => {
            toastService.addToast({
              type: 'error',
              message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
            });
          },
        }
      );
    }
  };

  const validateReturnInfoValues = () => {
    const { totalBox, totalBoxInput, receiverContactNumber, primaryAddress, returnPlace, accessMethod } =
      secondStepReturnInfoValues;

    const updatedState: TypeReturnRegisterSecondStepValuesState = {
      ...secondStepReturnInfoValuesState,
      ...addIf(!totalBox, 'totalBox', ERROR_REQUIRED_SELECT_STATUS),
      ...addIf(totalBox?.value === 'ETC' && !totalBoxInput, 'totalBoxInput', ERROR_REQUIRED_INPUT_STATUS),
      ...addIf(
        totalBox?.value === 'ETC' && totalBoxInput.trim() !== '' && Number(totalBoxInput) < CLAIM_TOTAL_BOX_COUNT.MIN,
        'totalBoxInput',
        {
          status: 'error',
          message: `${CLAIM_TOTAL_BOX_COUNT.MIN} 이상으로 입력해주세요.`,
        }
      ),
      ...addIf(
        totalBox?.value === 'ETC' && totalBoxInput.trim() !== '' && Number(totalBoxInput) > CLAIM_TOTAL_BOX_COUNT.MAX,
        'totalBoxInput',
        {
          status: 'error',
          message: `${CLAIM_TOTAL_BOX_COUNT.MAX} 이하로 입력해주세요.`,
        }
      ),
      ...addIf(!receiverContactNumber, 'receiverContactNumber', ERROR_REQUIRED_INPUT_STATUS),
      ...addIf(
        receiverContactNumber && !validateTelFormat(receiverContactNumber),
        'receiverContactNumber',
        ERROR_INVALID_FORMAT_INPUT_STATUS
      ),
      ...addIf(!primaryAddress, 'primaryAddress', ERROR_REQUIRED_INPUT_STATUS),
      ...addIf(!returnPlace, 'returnPlace', ERROR_REQUIRED_SELECT_STATUS),
      ...addIf(!accessMethod, 'accessMethod', ERROR_REQUIRED_SELECT_STATUS),

      returnOrderItems: secondStepReturnInfoValues.returnOrderItems.map((item) => ({
        itemId: item.itemId,
        returnQuantity: item.isReturn && !item.returnQuantity ? ERROR_STATUS : DEFAULT_INPUT_STATUS,
        returnReason: item.isReturn && !item.returnReason ? ERROR_STATUS : DEFAULT_INPUT_STATUS,
      })),
    };

    setSecondStepReturnInfoValuesState(updatedState);

    if (secondStepReturnInfoValues.returnOrderItems.every((item) => !item.isReturn)) {
      dialogService.addDialog({
        children: '반품 신청할 상품을 1개 이상 체크해주세요.',
      });
      return false;
    }

    if (
      updatedState.returnOrderItems.some(
        (item) => item.returnQuantity.status === 'error' || item.returnReason.status === 'error'
      )
    ) {
      dialogService.addDialog({
        children: '미입력한 반품 상품 정보가 있습니다.\n다시 확인 후 시도해주세요.',
      });
      return false;
    }

    const { returnOrderItems, ...states } = updatedState;
    const isValid = Object.values(states).every((item) => item.status !== 'error');

    if (!isValid) {
      focusFirstErrorField(errorFieldRefs.current, states, ERROR_FIELD_FOCUS_ORDER);
    }

    return isValid;
  };

  const handleConfirm = () => {
    const isValid = validateReturnInfoValues();

    if (!isValid) return;

    dialogService.addDialog({
      type: 'confirm',
      children: '등록 시 즉시 반품 처리가 시작됩니다.\n등록하시겠습니까?',
      confirmText: '등록',
      onConfirm: registerReturn,
    });
  };

  const registerReturn = () => {
    const {
      receiverName,
      receiverContactNumber,
      primaryAddress,
      detailAddress,
      accessMethod,
      accessDetail,
      returnPlace,
      returnPlaceDetail,
      courierMemo,
      returnOrderItems,
      totalBox,
      totalBoxInput,
      outboundCode,
    } = secondStepReturnInfoValues;

    const params: TypePostReturnOrdersParams = {
      member: {
        name: receiverName,
        phoneNumber: receiverContactNumber.replaceAll('-', ''),
      },
      returnPlace: {
        primaryAddress: primaryAddress,
        secondaryAddress: detailAddress,
        accessMethod: accessMethod?.value ?? '',
        accessDetail: accessDetail,
        pickupType: returnPlace?.value ?? '',
        pickupDetail: returnPlaceDetail,
        memo: courierMemo,
      },
      returnOrderItems: returnOrderItems
        .filter((item) => item.isReturn)
        .map((item) => ({
          goodsCode: item.itemId,
          quantity: Number(item.returnQuantity),
          reasonCode: item.returnReason?.value ?? '',
        })),
      totalBox: totalBox?.value === 'ETC' ? Number(totalBoxInput) : Number(totalBox?.value ?? 0),
    };

    registerReturnOrders(
      { params, pathParams: { outboundOrderCode: outboundCode } },
      {
        onSuccess: onConfirm,
        onError: (error) => {
          toastService.addToast({
            type: 'error',
            message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
          });
        },
      }
    );
  };

  return (
    <Modal
      title="반품 신청 등록"
      size="xlarge"
      open={isOpen}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmText="등록"
      step={{
        steps: [{ title: '반품 주문 선택' }, { title: '반품 정보 입력' }],
        currentStep,
        onStepChange: handleStepChange,
      }}
    >
      {currentStep === 1 && <ClaimRegisterFirstStepContent mode="return" setSelectedItem={setSelectedOrder} />}
      {currentStep === 2 && <ClaimReturnRegisterSecondStepContent errorFieldRefs={errorFieldRefs} />}
    </Modal>
  );
};

export default ClaimReturnRegisterModal;
